import React, { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast
} from '@chakra-ui/react'
import axios from 'lib/axios'
import { useBuildiumPostingGuard } from 'lib/buildiumBooksLock'
import { formatMoney } from 'lib/numbers'
import { DeleteButton } from '../LedgerRow/DeleteButton'
import { EditButton } from '../LedgerRow/EditButton'
import {
  AcceleratedRentEntry,
  AcceleratedRentPostResponse,
  AcceleratedRentPreview,
  AcceleratedRentPostingIssue,
  AcceleratedRentUnmatchedEntry,
  getErrorMessage,
  getLeaseSummary
} from './helpers'

type AcceleratedRentModalProps = {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void | Promise<void>
  rentalId: string
}

const statusColor = {
  missing: 'blue',
  matching: 'green',
  mismatched: 'orange',
  duplicate: 'red'
} as const

const statusLabel = {
  missing: 'Not on the ledger',
  matching: 'On the ledger',
  mismatched: 'Amount differs',
  duplicate: 'Duplicate charges'
} as const

const automaticOutcome = {
  posted: { color: 'green', label: 'Posted' },
  already_matching: { color: 'green', label: 'Already on the ledger' },
  no_charge: { color: 'gray', label: 'No charge needed' },
  review_required: { color: 'orange', label: 'Needs review' },
  awaiting_data: { color: 'orange', label: 'Waiting for rental information' },
  skipped_ineligible: { color: 'gray', label: 'Not eligible' }
} as const

const unmatchedEntryLabel = {
  manual: 'Added manually',
  stale: 'Outside this period',
  unclassified: 'Needs review'
} as const

const formatDate = (date: string) => moment.utc(date).format('MMM D, YYYY')
const formatPeriod = (startDate: string, endDate: string) =>
  `${moment.utc(startDate).format('MMM D')}–${moment.utc(endDate).format('MMM D')}`
const formatMonth = (month: string) => moment.utc(`${month}-01`).format('MMMM YYYY')

const getFriendlyErrorMessage = (error: unknown, fallback: string) => {
  const message = getErrorMessage(error, fallback)
  return message === 'Network Error' || /^Request failed with status code \d+$/.test(message)
    ? fallback
    : message
}

const formatMonthList = (months: string[]) =>
  months
    .map(formatMonth)
    .reduce(
      (list, month, index, formattedMonths) =>
        index === 0
          ? month
          : `${list}${index === formattedMonths.length - 1 ? ' and ' : ', '}${month}`,
      ''
    )

type RegularRentMismatchCheck = Extract<
  AcceleratedRentPreview['regularRentCheck'],
  { kind: 'mismatch' }
>

const hasMoveOutMonthRentMismatch = (check: RegularRentMismatchCheck) =>
  Math.abs(check.actual - check.expected) > check.tolerance

const formatPostMoveOutRegularRent = (check: RegularRentMismatchCheck) => {
  const totalsByMonth = check.postMoveOutRegularRentRows.reduce(
    (totals: Record<string, number>, row) => {
      const month = row.date.slice(0, 7)
      totals[month] = (totals[month] ?? 0) + row.amount
      return totals
    },
    {}
  )

  return Object.entries(totalsByMonth)
    .sort(([leftMonth], [rightMonth]) => leftMonth.localeCompare(rightMonth))
    .map(
      ([month, amount]) =>
        `${formatMoney(Number(amount.toFixed(2)), 2, '$')} of regular rent for ${formatMonth(
          month
        )}`
    )
    .reduce(
      (list, amount, index, amounts) =>
        index === 0 ? amount : `${list}${index === amounts.length - 1 ? ' and ' : ', '}${amount}`,
      ''
    )
}

const getIssueCopy = (issue: AcceleratedRentPostingIssue, preview: AcceleratedRentPreview) => {
  switch (issue.code) {
    case 'NO_ACCELERATED_WINDOW': {
      if (!preview.calculation?.moveOutDate || !preview.calculation.finalLiabilityDate) {
        const description = !preview.calculation?.moveOutDate
          ? preview.calculation?.finalLiabilityDate
            ? 'Add a move-out date to calculate accelerated rent.'
            : 'Add a move-out date and final liability date to calculate accelerated rent.'
          : 'Add a final liability date to calculate accelerated rent.'
        return {
          title: 'Move-out details are missing',
          description
        }
      }

      const description = !preview.calculation.hasFinalLiabilityAfterMoveOut
        ? "The resident's rent responsibility does not continue after move-out, so there is nothing to post."
        : preview.calculation.nextRentalOccupancyDate
        ? 'The next resident moves in before accelerated rent begins, so there is nothing to post.'
        : 'The current dates do not create an accelerated rent period.'
      return {
        title: 'No accelerated rent is expected',
        description
      }
    }
    case 'AGREEMENT_GAP': {
      const uncoveredDate = issue.message.match(/\d{4}-\d{2}-\d{2}/)?.[0]
      return {
        title: 'Lease dates need attention',
        description: uncoveredDate
          ? `No active lease covers ${formatDate(
              uncoveredDate
            )}. Update the lease dates before continuing.`
          : 'An active lease does not cover the full period. Update the lease dates before continuing.'
      }
    }
    case 'UNABLE_TO_DETERMINE_RENT':
      return {
        title: "Hudson couldn't calculate the rent",
        description: 'Check the active lease dates and rent amount, then try again.'
      }
    case 'DUPLICATE_ENTRIES': {
      const months = preview.expectedRows
        .filter((row) => row.status === 'duplicate')
        .map((row) => row.accrualMonth)
      return {
        title: 'Duplicate charges found',
        description:
          months.length > 0
            ? `Delete the extra charges for ${formatMonthList(months)} before continuing.`
            : 'Delete the extra charges before continuing.'
      }
    }
    case 'STALE_ENTRIES':
      return {
        title: 'Some charges are outside this period',
        description:
          'The current expected amount for those months is $0. Review the charges below and delete any that should no longer be on the ledger.'
      }
    case 'MANUAL_ENTRIES':
      return {
        title: 'Review manually added charges',
        description:
          preview.expectedRows.length > 0
            ? "Hudson found accelerated rent charges that aren't tied to an expected month. Review them below before continuing."
            : "Hudson found accelerated rent charges that aren't tied to an expected month. Review or remove them if they were added by mistake."
      }
    case 'MISMATCHED_ENTRIES': {
      const mismatchedRows = preview.expectedRows.filter((row) => row.status === 'mismatched')
      const months = mismatchedRows.map((row) => row.accrualMonth)
      const amount = mismatchedRows.reduce((total, row) => total + row.expectedAcceleratedRent, 0)
      return {
        title: 'A charge amount does not match',
        description: `$${amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })} will be added for ${formatMonthList(months)}. Existing charges will stay on the ledger.`
      }
    }
    case 'AWAITING_REGULAR_RENT':
      return {
        title: 'Post regular rent first',
        description:
          preview.regularRentCheck.ok === false &&
          preview.regularRentCheck.kind === 'awaiting_generation'
            ? `Regular rent for ${formatMonth(
                preview.regularRentCheck.moveOutMonth
              )} is not on the ledger yet. Add it, then try again.`
            : 'Regular rent for the move-out month is not on the ledger yet. Add it, then try again.'
      }
    case 'REGULAR_RENT_MISMATCH':
      if (preview.regularRentCheck.ok === false && preview.regularRentCheck.kind === 'mismatch') {
        const check = preview.regularRentCheck
        const moveOutMonth = formatMonth(check.moveOutMonth)
        const laterRegularRent = formatPostMoveOutRegularRent(check)
        const moveOutMonthMismatch = hasMoveOutMonthRentMismatch(check)

        if (laterRegularRent && !moveOutMonthMismatch) {
          return {
            title: 'Remove later regular rent first',
            description: `Regular rent for ${moveOutMonth} is correct. No regular rent should remain after ${moveOutMonth}, but the ledger has ${laterRegularRent}. Remove the later rent before continuing.`
          }
        }

        const moveOutMonthDescription = `The ledger has ${formatMoney(
          check.actual,
          2,
          '$'
        )} of regular rent for ${moveOutMonth}; the lease calls for ${formatMoney(
          check.expected,
          2,
          '$'
        )}.`

        if (laterRegularRent) {
          return {
            title: 'Correct regular rent first',
            description: `${moveOutMonthDescription} No regular rent should remain after ${moveOutMonth}, but the ledger has ${laterRegularRent}. Correct the ${moveOutMonth} amount and remove the later rent before continuing.`
          }
        }

        if (moveOutMonthMismatch) {
          return {
            title: 'Correct regular rent first',
            description: `${moveOutMonthDescription} Correct it before continuing.`
          }
        }
      }

      return {
        title: 'Correct regular rent first',
        description:
          'The regular rent on the ledger does not match the lease. Correct it before continuing.'
      }
    case 'LEDGER_IMMUTABLE':
      return {
        title: 'This ledger is closed',
        description: 'This rental has been finalized, so its charges can no longer be changed.'
      }
    default:
      return {
        title: 'Review needed',
        description: 'Hudson found something that needs attention before you continue.'
      }
  }
}

const isEditableDate = (date: string) => {
  const today = moment().format('YYYY-MM-DD')
  const entryDate = moment.utc(date).format('YYYY-MM-DD')
  return entryDate >= today || entryDate.slice(0, 7) === today.slice(0, 7)
}

export default function AcceleratedRentModal({
  isOpen,
  onClose,
  onSuccess,
  rentalId
}: AcceleratedRentModalProps) {
  const [preview, setPreview] = useState<AcceleratedRentPreview | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isPosting, setIsPosting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [acknowledgeLegacy, setAcknowledgeLegacy] = useState(false)
  const [postingDate, setPostingDate] = useState('')
  const [postingDateTouched, setPostingDateTouched] = useState(false)
  const previewRequestId = useRef(0)
  const toast = useToast()

  const loadPreview = useCallback(async () => {
    const requestId = previewRequestId.current + 1
    previewRequestId.current = requestId
    setIsLoadingPreview(true)
    setErrorMessage(undefined)

    try {
      const result = await axios.get<AcceleratedRentPreview>(
        `/admin/rentals/${rentalId}/accelerated_rent`
      )
      if (previewRequestId.current !== requestId) return

      setPreview(result.data)
      setAcknowledgeLegacy(false)
      setPostingDate(result.data.defaultPostingDate ?? '')
      setPostingDateTouched(false)
    } catch (error) {
      if (previewRequestId.current !== requestId) return
      setPreview(null)
      setErrorMessage(
        getFriendlyErrorMessage(
          error,
          "Hudson couldn't load accelerated rent. Check your connection and try again."
        )
      )
    } finally {
      if (previewRequestId.current === requestId) setIsLoadingPreview(false)
    }
  }, [rentalId])

  useEffect(() => {
    if (isOpen) {
      loadPreview()
      return
    }

    previewRequestId.current += 1
    setPreview(null)
    setErrorMessage(undefined)
    setAcknowledgeLegacy(false)
    setPostingDate('')
    setPostingDateTouched(false)
    setIsLoadingPreview(false)
  }, [isOpen, loadPreview])

  const refreshManagementAndLedger = useCallback(async () => {
    await Promise.all([loadPreview(), Promise.resolve(onSuccess?.())])
  }, [loadPreview, onSuccess])

  const acknowledgementIssues =
    preview?.postingIssues.filter((issue) => issue.severity === 'requires_acknowledgement') ?? []
  const hasBlockingIssue =
    preview?.postingIssues.some((issue) => issue.severity === 'blocking') ?? false
  const hasMissingOrMismatched =
    preview?.expectedRows.some((row) => row.status === 'missing' || row.status === 'mismatched') ??
    false
  const allRowsMatching =
    !!preview &&
    preview.expectedRows.length > 0 &&
    preview.expectedRows.every((row) => row.status === 'matching')
  const hasExpectedRows = (preview?.expectedRows.length ?? 0) > 0
  const action =
    hasBlockingIssue || !hasExpectedRows
      ? null
      : hasMissingOrMismatched
      ? 'post'
      : allRowsMatching && !preview?.postingCompletedAt
      ? 'mark_complete'
      : null
  const requiresPostingDate = action === 'post' && (preview?.totalToPost ?? 0) > 0
  const showAcknowledgement = !!action && acknowledgementIssues.length > 0
  const immutableIssue = preview?.postingIssues.find((issue) => issue.code === 'LEDGER_IMMUTABLE')
  const {
    booksLock,
    hasValidPostingDate,
    isBooksLockLoading,
    isBooksLockError,
    isBuildiumBooksLocked,
    retryBooksLock
  } = useBuildiumPostingGuard({
    date: postingDate,
    enabled: isOpen && requiresPostingDate,
    rentalId
  })
  const canSubmit =
    !!preview &&
    !!action &&
    !hasBlockingIssue &&
    (!showAcknowledgement || acknowledgeLegacy) &&
    (!requiresPostingDate ||
      (!!postingDate &&
        hasValidPostingDate &&
        !isBooksLockLoading &&
        !isBooksLockError &&
        !isBuildiumBooksLocked)) &&
    !isLoadingPreview &&
    !isPosting

  const automaticStatus = (preview?.automaticOutcome
    ? automaticOutcome[preview.automaticOutcome]
    : undefined) ?? {
    color: 'gray',
    label: 'Not run'
  }
  const noAcceleratedWindow =
    preview?.postingIssues.some((issue) => issue.code === 'NO_ACCELERATED_WINDOW') ?? false
  const defaultsToToday =
    !!preview && preview.defaultPostingDate !== preview.calculation?.acceleratedStartDate
  const minimumPostingDate = booksLock?.lockDate
    ? moment.utc(booksLock.lockDate).add(1, 'day').format('YYYY-MM-DD')
    : undefined
  const staleEntriesByMonth = new Map<string, AcceleratedRentUnmatchedEntry[]>()
  const otherUnmatchedEntries: AcceleratedRentUnmatchedEntry[] = []

  for (const entry of preview?.unmatchedEntries ?? []) {
    if (entry.classification === 'stale' && entry.accrualMonth) {
      staleEntriesByMonth.set(entry.accrualMonth, [
        ...(staleEntriesByMonth.get(entry.accrualMonth) ?? []),
        entry
      ])
    } else {
      otherUnmatchedEntries.push(entry)
    }
  }

  const tableRows = [
    ...(preview?.expectedRows ?? []).map((row) => ({
      kind: 'expected' as const,
      accrualMonth: row.accrualMonth,
      row
    })),
    ...Array.from(staleEntriesByMonth, ([accrualMonth, entries]) => ({
      kind: 'stale' as const,
      accrualMonth,
      entries
    }))
  ].sort((left, right) => left.accrualMonth.localeCompare(right.accrualMonth))

  const handlePost = async () => {
    if (!canSubmit) return

    setIsPosting(true)
    setErrorMessage(undefined)

    try {
      const result = await axios.post<AcceleratedRentPostResponse>(
        `/admin/rentals/${rentalId}/accelerated_rent`,
        {
          acknowledgeLegacy,
          ...(requiresPostingDate ? { postingDate } : {})
        }
      )

      if (result.data.result !== 'posted') {
        throw new Error(
          "Hudson couldn't complete the request. Review the latest details and try again."
        )
      }

      const createdCount = 'created' in result.data ? result.data.created.length : 0
      toast({
        title:
          createdCount > 0
            ? `Posted ${createdCount} accelerated rent ${createdCount === 1 ? 'charge' : 'charges'}`
            : 'Marked accelerated rent complete',
        status: 'success',
        duration: 3000,
        position: 'bottom-left'
      })
      await refreshManagementAndLedger()
    } catch (error) {
      const message = getFriendlyErrorMessage(
        error,
        'Nothing was posted. Check your connection and try again.'
      )
      await loadPreview()
      setErrorMessage(message)
    } finally {
      setIsPosting(false)
    }
  }

  const renderEntryControls = (entry: AcceleratedRentEntry) =>
    immutableIssue ? (
      <Text key={entry.id} fontSize='xs' color='gray.600'>
        Not available
      </Text>
    ) : (
      <Stack
        key={entry.id}
        spacing={1}
        align='flex-start'
        pointerEvents={isPosting ? 'none' : undefined}
        opacity={isPosting ? 0.5 : 1}
      >
        <HStack spacing={0}>
          {isEditableDate(entry.date) && !defaultsToToday && (
            <EditButton
              rentalId={rentalId}
              ledgerItem={entry}
              onSuccess={refreshManagementAndLedger}
            />
          )}
          <DeleteButton
            rentalId={rentalId}
            ledgerItem={entry}
            preferTodayReversal={defaultsToToday}
            onSuccess={refreshManagementAndLedger}
          />
        </HStack>
      </Stack>
    )

  const renderLedgerEntry = (entry: AcceleratedRentEntry) => (
    <HStack key={entry.id} minH='28px' spacing={1}>
      <Text fontSize='xs' whiteSpace='nowrap'>
        {formatDate(entry.date)} · {formatMoney(entry.amount, 2, '$')}
      </Text>
      {renderEntryControls(entry)}
    </HStack>
  )

  return (
    <Modal
      size='6xl'
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={!isPosting}
      closeOnOverlayClick={!isPosting}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={3}>
          <Text>Manage Accelerated Rent</Text>
          <Text mt={1} fontSize='sm' fontWeight='normal' color='gray.600'>
            Review the expected charges and resolve any issues before posting.
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={isPosting} />
        <Divider />
        <ModalBody>
          <Stack spacing={5} py={2}>
            {errorMessage && (
              <Alert status='error' borderRadius='md'>
                <AlertIcon />
                <HStack flex='1' justify='space-between' align='start'>
                  <Text>{errorMessage}</Text>
                  <Button
                    size='sm'
                    variant='outline'
                    colorScheme='red'
                    onClick={loadPreview}
                    isLoading={isLoadingPreview}
                  >
                    Try again
                  </Button>
                </HStack>
              </Alert>
            )}
            {isLoadingPreview && <Text color='gray.600'>Loading accelerated rent...</Text>}

            {preview && (
              <>
                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacing={4}
                  borderWidth='1px'
                  borderColor='gray.200'
                  borderRadius='md'
                  px={4}
                  py={3}
                >
                  <Stack spacing={0}>
                    <Text fontSize='xs' color='gray.600'>
                      Automatic posting
                    </Text>
                    <HStack mt={1}>
                      <Badge colorScheme={automaticStatus.color}>{automaticStatus.label}</Badge>
                    </HStack>
                    {preview.automaticAttemptedAt && (
                      <Text mt={1} fontSize='xs' color='gray.600'>
                        Last checked{' '}
                        {moment(preview.automaticAttemptedAt).format('MMM D, YYYY h:mm A')}
                      </Text>
                    )}
                  </Stack>
                  <Stack spacing={0}>
                    <Text fontSize='xs' color='gray.600'>
                      Posting status
                    </Text>
                    <HStack mt={1}>
                      <Badge colorScheme={preview.postingCompletedAt ? 'green' : 'gray'}>
                        {preview.postingCompletedAt ? 'Complete' : 'Not complete'}
                      </Badge>
                    </HStack>
                    {preview.postingCompletedAt && (
                      <Text mt={1} fontSize='xs' color='gray.600'>
                        Completed {moment(preview.postingCompletedAt).format('MMM D, YYYY h:mm A')}
                      </Text>
                    )}
                  </Stack>
                </SimpleGrid>

                {preview.postingIssues.map((issue) => {
                  const copy = getIssueCopy(issue, preview)
                  return (
                    <Alert
                      key={issue.code}
                      status={
                        issue.severity === 'blocking'
                          ? 'error'
                          : issue.severity === 'requires_acknowledgement' ||
                            issue.code === 'STALE_ENTRIES'
                          ? 'warning'
                          : 'info'
                      }
                      alignItems='start'
                      borderRadius='md'
                    >
                      <AlertIcon mt={0.5} />
                      <Stack spacing={1}>
                        <Text fontWeight='semibold'>{copy.title}</Text>
                        <Text fontSize='sm'>{copy.description}</Text>
                      </Stack>
                    </Alert>
                  )
                })}

                {preview.expectedRows.length > 0 &&
                  preview.calculation?.nextRentalOccupancyDate && (
                    <Alert status='info'>
                      <AlertIcon />
                      The accelerated rent period ends{' '}
                      {formatDate(preview.calculation.acceleratedEndDate!)}, because a new resident
                      moves in on {formatDate(preview.calculation.nextRentalOccupancyDate)}.
                    </Alert>
                  )}

                {tableRows.length > 0 ? (
                  <TableContainer>
                    <Table size='sm'>
                      <Thead>
                        <Tr>
                          <Th>Rent month</Th>
                          <Th>Period</Th>
                          <Th w='100px' px={2} whiteSpace='normal'>
                            Monthly rent
                          </Th>
                          <Th w='110px' px={2} whiteSpace='normal' isNumeric>
                            Expected charge
                          </Th>
                          <Th>Ledger status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {tableRows.map((tableRow) =>
                          tableRow.kind === 'expected' ? (
                            <Tr key={tableRow.accrualMonth}>
                              <Td>{formatMonth(tableRow.accrualMonth)}</Td>
                              <Td>
                                {formatPeriod(
                                  tableRow.row.periodStartDate,
                                  tableRow.row.periodEndDate
                                )}
                              </Td>
                              <Td px={2}>{getLeaseSummary(tableRow.row.stepUps)}</Td>
                              <Td px={2} isNumeric>
                                {formatMoney(tableRow.row.expectedAcceleratedRent, 2, '$')}
                              </Td>
                              <Td>
                                <Stack spacing={1}>
                                  <Badge
                                    alignSelf='flex-start'
                                    colorScheme={statusColor[tableRow.row.status]}
                                  >
                                    {tableRow.row.status === 'matching' &&
                                    tableRow.row.entries.length === 0
                                      ? 'No charge'
                                      : statusLabel[tableRow.row.status]}
                                  </Badge>
                                  {tableRow.row.entries.map(renderLedgerEntry)}
                                </Stack>
                              </Td>
                            </Tr>
                          ) : (
                            <Tr key={`stale-${tableRow.accrualMonth}`}>
                              <Td>{formatMonth(tableRow.accrualMonth)}</Td>
                              <Td color='gray.600'>—</Td>
                              <Td px={2} color='gray.600'>
                                —
                              </Td>
                              <Td px={2} isNumeric>
                                {formatMoney(0, 2, '$')}
                              </Td>
                              <Td>
                                <Stack spacing={1}>
                                  <Badge alignSelf='flex-start' colorScheme='orange'>
                                    Outside period
                                  </Badge>
                                  {tableRow.entries.map(renderLedgerEntry)}
                                </Stack>
                              </Td>
                            </Tr>
                          )
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                ) : !noAcceleratedWindow && preview.postingIssues.length === 0 ? (
                  <Text fontSize='sm' color='gray.600'>
                    There are no accelerated rent charges to review.
                  </Text>
                ) : null}

                {otherUnmatchedEntries.length > 0 && (
                  <Stack spacing={2}>
                    <Text fontWeight='semibold'>Other accelerated rent charges</Text>
                    {otherUnmatchedEntries.map((entry) => (
                      <Box
                        key={entry.id}
                        borderWidth='1px'
                        borderColor='gray.200'
                        borderRadius='md'
                        px={3}
                        py={2}
                      >
                        <HStack justify='space-between' align='start'>
                          <Stack spacing={1}>
                            <HStack>
                              <Badge>{unmatchedEntryLabel[entry.classification]}</Badge>
                              <Text fontSize='sm'>
                                {formatDate(entry.date)} · {formatMoney(entry.amount, 2, '$')}
                              </Text>
                            </HStack>
                            {entry.note && (
                              <Text fontSize='xs' color='gray.600'>
                                {entry.note}
                              </Text>
                            )}
                          </Stack>
                          {renderEntryControls(entry)}
                        </HStack>
                      </Box>
                    ))}
                  </Stack>
                )}

                {showAcknowledgement && (
                  <Checkbox
                    isChecked={acknowledgeLegacy}
                    onChange={(event) => setAcknowledgeLegacy(event.target.checked)}
                  >
                    {action === 'post'
                      ? 'I reviewed the charges above. Hudson will add the amount shown and leave the existing charges unchanged.'
                      : 'I reviewed the charges above and want to mark accelerated rent complete without changing them.'}
                  </Checkbox>
                )}

                {requiresPostingDate && (
                  <>
                    <FormControl
                      isRequired
                      isDisabled={isPosting}
                      isInvalid={
                        (postingDateTouched && !hasValidPostingDate) || isBuildiumBooksLocked
                      }
                    >
                      <FormLabel>Charge date</FormLabel>
                      <Input
                        maxW='240px'
                        type='date'
                        min={minimumPostingDate}
                        value={postingDate}
                        onChange={(event) => {
                          setPostingDateTouched(true)
                          setPostingDate(event.target.value)
                        }}
                      />
                      <FormHelperText>
                        {defaultsToToday ? (
                          <>
                            Will post accelerated rent charges as of this date.
                            <br />
                            Defaults to today because the move-out month has passed.
                          </>
                        ) : (
                          'Post accelerated rent as of this date. Defaults to the day after move-out.'
                        )}
                      </FormHelperText>
                      <FormErrorMessage>
                        {postingDate && hasValidPostingDate
                          ? 'Choose a date after the closed Buildium period.'
                          : 'Choose a valid charge date.'}
                      </FormErrorMessage>
                    </FormControl>
                    {isBooksLockLoading && (
                      <Text fontSize='sm' color='gray.600'>
                        Checking whether this date is open in Buildium...
                      </Text>
                    )}
                    {(isBooksLockError || isBuildiumBooksLocked) && (
                      <Alert status='error' borderRadius='md'>
                        <AlertIcon />
                        <HStack flex='1' justify='space-between' align='start'>
                          <Text>
                            {isBooksLockError
                              ? "Hudson couldn't check whether this date is open in Buildium. Try again or ask FinOps for the latest closed date."
                              : `Buildium is closed through ${booksLock?.lockDate}. Choose a later date, or ask FinOps to reopen the period.`}
                          </Text>
                          {isBooksLockError && (
                            <Button
                              size='sm'
                              variant='outline'
                              colorScheme='red'
                              onClick={() => retryBooksLock()}
                            >
                              Try again
                            </Button>
                          )}
                        </HStack>
                      </Alert>
                    )}
                  </>
                )}

                {action === 'post' && (
                  <HStack justify='flex-end'>
                    <Text color='gray.600'>Amount to post</Text>
                    <Text fontWeight='semibold'>{formatMoney(preview.totalToPost, 2, '$')}</Text>
                  </HStack>
                )}
              </>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant='outline' onClick={onClose} isDisabled={isPosting}>
              Close
            </Button>
            {action && (
              <Button
                colorScheme='teal'
                onClick={handlePost}
                isDisabled={!canSubmit}
                isLoading={isPosting}
              >
                {action === 'mark_complete' ? 'Mark posting complete' : 'Post missing charges'}
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
